import { useEffect, useState } from 'preact/hooks';
import { MediaImage } from '../components/MediaEl';
import {
  ApiError,
  getResume,
  createResumeEntry,
  deleteResumeEntry,
  type Resume,
  type ResumeVariant,
  type ResumeSectionKind,
} from '../lib/api';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { mstr, mediumLabel, summariseAwards } from './materialsPresentation';

export interface MaterialsResumeProps {
  tenantId: string;
}

const VARIANTS: { id: ResumeVariant; label: string }[] = [
  { id: 'full', label: 'Full' },
  { id: 'theatrical', label: 'Theatrical' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'voice', label: 'Voice' },
];

// B5b — RESUME: the auto-assembled resume for the chosen variant. Credits come
// grouped (Film / Television / Voice-Animation / …) straight from the backend's
// assembly; the variant switcher refetches with ?variant=. Training / Awards /
// Special skills are editable (small add forms feeding the CRUD endpoints, a ×
// to remove). Awards collapse into "2× CSA Nominee"-style lines. Export is a
// PRINT STYLESHEET (window.print() + an @media print block in Materials.css that
// shows ONLY .print-resume) — no external PDF lib, CSP-safe.
export function MaterialsResume({ tenantId }: MaterialsResumeProps) {
  const [variant, setVariant] = useState<ResumeVariant>('full');
  const [resume, setResume] = useState<Resume | null>(null);
  const [loading, setLoading] = useState(true);
  // A failed read is a different fact from an empty resume — the empty state
  // must never stand in for a refusal (S15's rule).
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  function load(v: ResumeVariant) {
    let cancelled = false;
    setLoading(true);
    void getResume(tenantId, v)
      .then((r) => {
        if (!cancelled) {
          setResume(r);
          setFailure(null);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setResume(null);
          setFailure(describeReadFailure(err));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }

  useEffect(() => load(variant), [tenantId, variant]);

  async function addEntry(kind: ResumeSectionKind, fields: Record<string, unknown>) {
    await createResumeEntry(tenantId, kind, fields);
    load(variant);
  }

  async function removeEntry(kind: ResumeSectionKind, id: string) {
    setRemoveError(null);
    try {
      await deleteResumeEntry(tenantId, kind, id);
    } catch (err) {
      setRemoveError(
        `The entry is still on the resume — ${err instanceof ApiError ? err.message : 'the server could not be reached'}`
      );
      return;
    }
    load(variant);
  }

  const awardLines = resume ? summariseAwards(resume.awards) : [];
  const skillNames = resume ? resume.skills.map((s) => mstr(s.fields, 'name')).filter(Boolean) : [];
  const hasAnything =
    resume &&
    (resume.creditGroups.length > 0 ||
      resume.training.length > 0 ||
      resume.awards.length > 0 ||
      resume.skills.length > 0 ||
      resume.headshot);

  return (
    <div class="materials__scroll">
      <div class="resume__toolbar">
        <div class="resume__variants" role="tablist" aria-label="Resume variant">
          {VARIANTS.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={v.id === variant}
              class={`resume__variant${v.id === variant ? ' resume__variant--on' : ''}`}
              onClick={() => setVariant(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          class="resume__export"
          onClick={() => window.print()}
          disabled={loading || !hasAnything}
        >
          Print
        </button>
      </div>

      {loading ? (
        <div class="materials__empty">Building your resume…</div>
      ) : failure ? (
        <div class="materials__error">
          {failure.headline} {failure.detail}
        </div>
      ) : !hasAnything ? (
        <div class="materials__empty">
          No resume yet. It builds itself from your bookings — add training, awards and skills below as they come.
        </div>
      ) : (
        <div class="resume print-resume">
          <div class="resume__head">
            {resume!.headshot ? (
              <div class="resume__headshot">
                <MediaImage tenantId={tenantId} id={resume!.headshot.mediaId} alt={resume!.headshot.label ?? 'Headshot'} />
              </div>
            ) : null}
            <div class="resume__headtext">
              {awardLines.length > 0 ? (
                <div class="resume__awardline">{awardLines.map((a) => a.text).join(' · ')}</div>
              ) : null}
            </div>
          </div>

          {resume!.creditGroups.map((g) => (
            <div key={g.group} class="resume__section">
              <div class="resume__sec">{g.group}</div>
              <table class="resume__table">
                <tbody>
                  {g.credits.map((c) => (
                    <tr key={c.projectId}>
                      <td class="resume__t">{c.production ?? '—'}</td>
                      <td class="resume__r">{c.role ?? ''}</td>
                      <td class="resume__c">{c.company ?? mediumLabel(c.medium)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {resume!.training.length > 0 ? (
            <div class="resume__section">
              <div class="resume__sec">Training</div>
              <table class="resume__table">
                <tbody>
                  {resume!.training.map((t) => (
                    <tr key={t.id}>
                      <td class="resume__t">{mstr(t.fields, 'name') ?? '—'}</td>
                      <td class="resume__r">{mstr(t.fields, 'org') ?? ''}</td>
                      <td class="resume__c">{mstr(t.fields, 'level') ?? (t.fields['is_current'] === true ? 'current' : '')}</td>
                      <td class="resume__del">
                        <button type="button" class="resume__x" aria-label="Remove training" onClick={() => removeEntry('training', t.id)}>
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {resume!.awards.length > 0 ? (
            <div class="resume__section">
              <div class="resume__sec">Awards</div>
              <table class="resume__table">
                <tbody>
                  {resume!.awards.map((a) => (
                    <tr key={a.id}>
                      <td class="resume__t">{mstr(a.fields, 'title') ?? '—'}</td>
                      <td class="resume__r">{mstr(a.fields, 'org') ?? ''}</td>
                      <td class="resume__c">{mstr(a.fields, 'year') ?? ''}</td>
                      <td class="resume__del">
                        <button type="button" class="resume__x" aria-label="Remove award" onClick={() => removeEntry('awards', a.id)}>
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {skillNames.length > 0 ? (
            <div class="resume__section">
              <div class="resume__sec">Special skills</div>
              <div class="resume__skills">
                {resume!.skills.map((s) => (
                  <span key={s.id} class="resume__skill">
                    {mstr(s.fields, 'name')}
                    <button type="button" class="resume__x" aria-label="Remove skill" onClick={() => removeEntry('skills', s.id)}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {removeError ? <div class="materials__error">{removeError}</div> : null}
      <AddForms onAdd={addEntry} />
    </div>
  );
}

// The three tiny entry forms behind Training / Awards / Special skills — kept
// out of the printed resume (they live below .print-resume, hidden at print
// time). Each is a <details> so the resume itself stays the focus.
function AddForms({ onAdd }: { onAdd: (kind: ResumeSectionKind, fields: Record<string, unknown>) => Promise<void> }) {
  return (
    <div class="resume-add">
      <div class="materials__grp">Fill your resume</div>
      <AddForm
        summary="Add training"
        fieldNames={['name', 'org', 'level']}
        placeholders={['Discipline / class', 'School / coach', 'Level']}
        required="name"
        onAdd={(fields) => onAdd('training', fields)}
      />
      <AddForm
        summary="Add award"
        fieldNames={['title', 'org', 'year']}
        placeholders={['Award', 'Body (e.g. CSA)', 'Year']}
        required="title"
        onAdd={(fields) => onAdd('awards', fields)}
      />
      <AddForm
        summary="Add special skill"
        fieldNames={['name']}
        placeholders={['Skill (e.g. Stage combat)']}
        required="name"
        onAdd={(fields) => onAdd('skills', fields)}
      />
    </div>
  );
}

function AddForm({
  summary,
  fieldNames,
  placeholders,
  required,
  onAdd,
}: {
  summary: string;
  fieldNames: string[];
  placeholders: string[];
  required: string;
  onAdd: (fields: Record<string, unknown>) => Promise<void>;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    const fields: Record<string, unknown> = {};
    for (const n of fieldNames) {
      const v = (values[n] ?? '').trim();
      if (v) fields[n] = v;
    }
    if (!fields[required]) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(fields);
      setValues({});
    } catch (err) {
      setError(
        `Nothing was added — ${err instanceof ApiError ? err.message : 'the server could not be reached'}`
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <details class="resume-add__item">
      <summary class="resume-add__summary">{summary}</summary>
      <form class="resume-add__form" onSubmit={submit}>
        {fieldNames.map((n, i) => (
          <input
            key={n}
            class="resume-add__input"
            type="text"
            placeholder={placeholders[i]}
            aria-label={placeholders[i]}
            value={values[n] ?? ''}
            onInput={(e) => setValues((prev) => ({ ...prev, [n]: (e.currentTarget as HTMLInputElement).value }))}
          />
        ))}
        <button type="submit" class="resume-add__save" disabled={busy}>
          {busy ? 'Saving…' : 'Add'}
        </button>
        {error ? <div class="materials__error">{error}</div> : null}
      </form>
    </details>
  );
}
